import React, { useState, useMemo } from "react";
import { useAuth } from "react-oidc-context";
import dropIcon from "../../../assets/drop.png";
import arrowIcon from "../../../assets/Icone_vert.png";
import EventBadge from "./EventBadge";

const formatDateDMY = (dateInput) => {
  if (!dateInput) return "-";
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Formater l'heure au format HH:MM:SS
const formatDateTime = (dateInput) => {
  if (!dateInput) return "-";
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("fr-FR");
};

// Formater la durée en secondes en format lisible (ex: "1h 5m" ou "5m 30s")
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return "0s";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && hours === 0) parts.push(`${secs}s`); // Afficher les secondes seulement si < 1h

  return parts.length > 0 ? parts.join(" ") : "0s";
};

const API_URL = process.env.REACT_APP_API_URL || "/api";

export default function StayCard({ stay, events = [], onDeleted }) {
  const [open, setOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const auth = useAuth();
  const token = auth.user?.access_token;
  // Décalage horizontal des éléments déroulés pour un positionnement entre gauche et centre
  const EXPANDED_ROW_OFFSET_PX = -500; // 0 = centré, négatif = vers la gauche, positif = vers la droite
  const { sId, rRoomNumber, checkIn, checkOut, endDate, status, actionsAvailableCount, actionsDoneCount, rewardsUnlockedCount, rewardsUsedCount, rewardsUnlockedTotal, totalSessionDurationSeconds, hotelName } = stay;
  const stayEndDate = checkOut || endDate || null;
  const hasRoomNumber = rRoomNumber !== null && rRoomNumber !== undefined && String(rRoomNumber).trim() !== "";
  const primaryStayLabel = hasRoomNumber ? rRoomNumber : `sId ${sId}`;

  const actionsPct = actionsAvailableCount ? Math.round((actionsDoneCount / actionsAvailableCount) * 100) : 0;

  // Indicateur d'activité (au moins une action/récompense réalisée/débloquée/utilisée)
  const hasActivity = Array.isArray(events) && events.length > 0;

  // N'afficher le badge "Création" qu'une seule fois par séjour
  // Exclure les événements "utilisation" pour les récompenses "don d'eau" (EAU)
  const filteredEvents = useMemo(() => {
    let creationShown = false;
    return (events || []).filter((ev) => {
      if (ev?.eventType === "CREATION") {
        if (creationShown) return false;
        creationShown = true;
      }
      // Exclure uniquement le doublon d'événement "utilisation" pour les récompenses EAU.
      // On conserve l'événement de déblocage, qui peut être marqué comme utilisé/complété.
      if (ev?.rwType === "EAU") {
        if (ev.eventType === "REWARD_USE") {
          return false; // Ne pas afficher l'événement utilisation pour don d'eau
        }
      }
      return true;
    });
  }, [events]);

  // Indicateurs d'activité pour le code couleur (petit point à côté du numéro de chambre)
  const hasActionEvent = filteredEvents.some((ev) => ev?.eventType === "ACTION");

  const hasRewardEvent = filteredEvents.some(
    (ev) => ev?.eventType === "REWARD_USE" || ev?.eventType === "REWARD_UNLOCK"
  );

  const headerActivityColor =
    hasActionEvent && hasRewardEvent
      ? "#7B68EE" // troisième couleur quand Actions + Récompense
      : hasActionEvent
      ? "#96B77A" // Actions
      : hasRewardEvent
      ? "#92937F" // Récompense
      : null;

  if (deleted) return null;

  const handleDelete = (e) => {
    e.stopPropagation();
    if (isDeleting) return;
    setShowConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/greenmiles/stays/${sId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && (data.success === true || data.success === "OK")) {
        window.location.reload();
      } else {
        console.error("Delete stay failed", { status: response.status, data });
        const msg = data.message || data.error || "Erreur lors de la suppression du séjour";
        alert(`Suppression impossible (${response.status}) : ${msg}`);
      }
    } catch (err) {
      console.error("Network error deleting stay", err);
      alert(err?.message || "Erreur réseau lors de la suppression");
    } finally {
      setIsDeleting(false);
      setShowConfirm(false);
    }
  };

  const handleCancelDelete = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (isDeleting) return;
    setShowConfirm(false);
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-100">
      {/* Bandeau résumé */}
      <button className="w-full text-left px-3 py-3" onClick={() => setOpen((v) => !v)} style={{ position: "relative" }}>
        <div className="grid grid-cols-12 items-center text-sm" style={{ columnGap: "12px", width: 900, position: "relative" }}>
          <div className="col-span-2 flex flex-col whitespace-nowrap">
            <div className="flex items-center">
              {headerActivityColor && (
                <span
                  className="mr-2 inline-block"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "9999px",
                    backgroundColor: headerActivityColor,
                  }}
                />
              )}
              {hasRoomNumber ? <span className="text-sm mr-1">N°</span> : <span className="text-sm mr-1">Séjour</span>}
              <span className="font-semibold text-lg">{primaryStayLabel}</span>
              {hasRoomNumber && (
                <span className="ml-2 text-xs italic" style={{ color: "#636462" }}>
                  sId {sId}
                </span>
              )}
            </div>
            {hotelName && (
              <span className="text-xs text-[#636462] mt-1 truncate" title={hotelName}>
                {hotelName}
              </span>
            )}
          </div>
          <div className="col-span-1 flex items-center">{/* point d'état déplacé à droite, à côté de la poubelle */}</div>
          {/* Bloc Check in / Check out / % d'actions sur une seule ligne avec espace contrôlé */}
          <div className="col-span-4" style={{ color: "#636462" }}>
            <div className="flex items-start gap-8">
              <div className="flex flex-col">
                <span className="text-xs italic">Check in</span>
                <span className="italic font-semibold">{formatDateDMY(checkIn)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs italic">Check out</span>
                <span className="italic font-semibold">{stayEndDate ? formatDateDMY(stayEndDate) : "-"}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs italic">% d'actions réalisées</span>
                <span className="italic font-semibold whitespace-nowrap">
                  {actionsPct} % ({actionsDoneCount}/{actionsAvailableCount})
                </span>
              </div>
            </div>
          </div>
          <div className="col-span-2 flex flex-col pl-4" style={{ color: "#636462" }}>
            <span className="text-xs italic"># récompenses utilisées</span>
            <span className="italic font-semibold">
              {rewardsUsedCount} / {rewardsUnlockedTotal ?? rewardsUnlockedCount}
            </span>
          </div>
          <div className="col-span-2 flex flex-col" style={{ color: "#636462" }}>
            <span className="text-xs italic">Temps total sur l'app</span>
            <span className="italic font-semibold">{formatDuration(totalSessionDurationSeconds || 0)}</span>
          </div>
          <div className="col-span-1 flex flex-col" style={{ color: "#636462" }}>
            <span className="text-xs italic">Statut</span>
            <span className="italic font-semibold">{status || "-"}</span>
          </div>
          {/* Delete icon moved outside grid to avoid impacting layout */}
        </div>
        {/* Toggle arrow icon (rotates with open/closed) */}
        <img src={arrowIcon} alt="Statut ouverture" style={{ position: "absolute", right: 36, top: "50%", transform: `translateY(-50%) rotate(${open ? "0deg" : "90deg"})`, width: 18, height: 18, transition: "transform 150ms ease", pointerEvents: "none" }} />
        <img src={dropIcon} alt="Supprimer le séjour" onClick={handleDelete} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 18, height: 18, opacity: isDeleting ? 0.5 : 1 }} className="cursor-pointer" />
      </button>

      {/* Contenu détaillé */}
      {open && (
        <div className="pb-3 space-y-1" style={{ width: 900 }}>
          {filteredEvents.map((ev) => (
            <div key={ev.eventAt} className="grid grid-cols-12 items-center text-sm px-1 py-1" style={{ columnGap: "6px" }}>
              <div className="col-span-2" />
              <div className="col-span-1" />
              <div className="col-span-2 flex justify-start">
                <EventBadge type={ev.eventType} />
              </div>
              <div className="col-span-4 font-semibold italic text-left pl-0" style={{ color: "#636462" }}>
                {ev.eventType === "ACTION" ? (
                  <>
                    {ev.actName || ev.eventType}
                    {ev.response ? ` – ${ev.response}` : ""}
                  </>
                ) : ev.eventType === "BUTTON_CLICK" ? (
                  "Clic sur le bouton LuniButton"
                ) : ev.eventType === "QR_SCAN" ? (
                  "Connexion via scan QR (premier scan)"
                ) : ev.eventType === "QR_RESCAN" ? (
                  "Reconnexion via re-scan QR"
                ) : ev.eventType === "RECONNECTION" ? (
                  "Reconnexion via la page"
                ) : ev.eventType === "DEPARTURE" ? (
                  "Départ"
                ) : (
                  (() => {
                    const name = ev.rwName || "Récompense";
                    const srwUsedStr = ev.srwUsed != null ? String(ev.srwUsed) : undefined;
                    const isUse = ev.eventType === "REWARD_USE" || srwUsedStr === "1" || ev.srwUsed === true;
                    const isUnlock = ev.eventType === "REWARD_UNLOCK" || srwUsedStr === "0";
                    if (ev.eventType === "CREATION") return "Arrivée sur la web app";
                    if (isUse) return `Utilisation : ${name}`;
                    if (isUnlock) return `Déblocage : ${name}`;
                    return name;
                  })()
                )}
              </div>
              <div className="col-span-3 text-right whitespace-nowrap text-xs font-semibold italic pl-0" style={{ color: "#636462" }}>
                {ev.eventType === "REWARD_USE" && (ev.srwUsed === 1 || String(ev.srwUsed) === "1" || ev.srwUsed === true) && !ev.srwUsedAt ? "-" : formatDateTime(ev.eventAt || ev.srwUsedAt || ev.srwCreatedAt || checkIn)}
              </div>
            </div>
          ))}
        </div>
      )}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.4)", zIndex: 50 }} onClick={handleCancelDelete}>
          <div className="bg-white rounded-lg shadow-lg" style={{ width: 360 }} onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-100 font-semibold">Confirmer la suppression</div>
            <div className="px-4 py-4 text-sm" style={{ color: "#636462" }}>
              Êtes-vous sûr de vouloir supprimer ce séjour ? Cette action supprimera définitivement :
              <div className="mt-2">
                <ul className="list-disc pl-5">
                  <li>Le séjour sId {sId}</li>
                  <li>Son profil LuniGreen</li>
                  <li>Toutes ses actions (actionsDone)</li>
                  <li>Toutes ses récompenses (stayReward)</li>
                </ul>
              </div>
            </div>
            <div className="px-4 py-3 flex justify-end gap-2 border-t border-gray-100">
              <button className="px-3 py-1 rounded border" onClick={handleCancelDelete} disabled={isDeleting}>
                Annuler
              </button>
              <button className="px-3 py-1 rounded text-white" style={{ backgroundColor: isDeleting ? "#9CA3AF" : "#DC2626" }} onClick={handleConfirmDelete} disabled={isDeleting}>
                {isDeleting ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
